import React from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Home as HomeIcon,
  ShoppingBag,
  Store as POSIcon,
  Package,
  Menu as MenuIcon
} from 'lucide-react';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import './MobileBottomNav.css';

/**
 * Mobile bottom-tab navigation.
 *
 * Mirrors the mobile app's tab order (mobile/src/navigation/MainNavigator.tsx):
 *   Home (Dashboard) — Orders — POS (center, floating, feature-gated) — Products — More
 *
 * Visibility:
 *   - Shown at < 1024px via CSS @media; hidden at >= 1024px (sidebar takes over).
 *   - POS tab hidden entirely when merchant lacks `sales.pos` feature access
 *     (FREE tier). Layout shifts to 4 tabs in that case.
 *
 * The "More" tab opens the existing sidebar drawer (Shell.js owns the
 * isMobileMenuOpen state) — passed in as `onMoreClick` so this component
 * stays presentational.
 */
const MobileBottomNav = ({ onMoreClick }) => {
  const location = useLocation();
  const { hasAccess: hasPOS } = useFeatureAccess('sales.pos');
  // Merchant discovery preference — hide POS from the floating center
  // button when the merchant dismissed it at the /pos first-visit
  // prompt or via Settings > Business preferences. Cosmetic-only; the
  // sales.pos feature gate above still controls actual access.
  const store = useSelector((s) => s.store.current);
  const posDismissed = store?.settings?.posOnboardingChoice === 'dismissed';
  const showPOS = hasPOS && !posDismissed;

  // Active match — startsWith so /orders/123 still highlights Orders.
  const isActive = (path) => {
    if (path === '/dashboard') {
      // Both /dashboard and /overview light up Home — they're the merchant's
      // landing surfaces. (More precise: /overview as the primary landing
      // post-onboarding, /dashboard as KPIs.)
      return location.pathname === '/dashboard' || location.pathname === '/overview';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <nav className="mbn" aria-label="Primary mobile navigation">
      <NavLink
        to="/overview"
        className={`mbn-tab ${isActive('/dashboard') ? 'mbn-tab-active' : ''}`}
        aria-label="Home"
      >
        <HomeIcon size={22} strokeWidth={2} />
        <span className="mbn-label">Home</span>
      </NavLink>

      <NavLink
        to="/orders"
        className={`mbn-tab ${isActive('/orders') ? 'mbn-tab-active' : ''}`}
        aria-label="Orders"
      >
        <ShoppingBag size={22} strokeWidth={2} />
        <span className="mbn-label">Orders</span>
      </NavLink>

      {showPOS && (
        <NavLink
          to="/pos"
          className={`mbn-tab mbn-tab-pos ${isActive('/pos') ? 'mbn-tab-active' : ''}`}
          aria-label="Point of Sale"
        >
          <span className="mbn-pos-button">
            <POSIcon size={22} strokeWidth={2} />
          </span>
          <span className="mbn-label mbn-label-pos">POS</span>
        </NavLink>
      )}

      <NavLink
        to="/products"
        className={`mbn-tab ${isActive('/products') ? 'mbn-tab-active' : ''}`}
        aria-label="Products"
      >
        <Package size={22} strokeWidth={2} />
        <span className="mbn-label">Products</span>
      </NavLink>

      <button
        type="button"
        className="mbn-tab mbn-tab-more"
        onClick={onMoreClick}
        aria-label="Open menu"
      >
        <MenuIcon size={22} strokeWidth={2} />
        <span className="mbn-label">Menu</span>
      </button>
    </nav>
  );
};

export default MobileBottomNav;
